import { useEffect, useState } from "react";
import { Box, Text } from "@radix-ui/themes";

interface Props {
  /** The markdown being edited, not what was last saved. */
  body: string;
  /** ClubManager::BlogsController#preview. */
  previewPath: string;
}

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
  "";

const DEBOUNCE_MS = 300;

/**
 * The pane beside the editor, rendered by the server.
 *
 * The HTML here is the *same* HTML the public post emits — same kramdown-GFM
 * dialect, same sanitizer allow list, same YouTube embeds — because it comes
 * from the one renderer rather than a JavaScript approximation of it. That is
 * also why `dangerouslySetInnerHTML` is safe and appears nowhere else in this
 * codebase: every byte below came out of Rails' `sanitize`, and the only iframe
 * it can contain was built from a validated YouTube id.
 *
 * If the video box renders empty, the CSP is wrong rather than the markdown —
 * `frame-src` has to name youtube-nocookie (config/initializers/content_security_policy.rb).
 */
export default function MarkdownPreview({ body, previewPath }: Props) {
  const [html, setHtml] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!body.trim()) {
      setHtml("");
      setFailed(false);
      return;
    }

    // One controller per keystroke run: a slow response for an older body must
    // not land on top of a newer one.
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(previewPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({ body }),
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`${response.status}`);
          return response.json();
        })
        .then((data) => {
          setHtml(data.html ?? "");
          setFailed(false);
        })
        .catch((error) => {
          // An abort is this effect being superseded, not a failure worth
          // reporting — the next run is already in flight.
          if (error.name !== "AbortError") setFailed(true);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [body, previewPath]);

  if (failed) {
    return (
      <Text size="2" color="gray">
        Preview unavailable right now — your post is unaffected.
      </Text>
    );
  }

  if (!html) {
    return (
      <Text size="2" color="gray">
        Start writing and the preview appears here.
      </Text>
    );
  }

  // `prose` does the headings, lists and tables; `.fbb__video` is defined
  // globally in application.css, because the wrapper comes from the server.
  return (
    <Box
      className="prose max-w-none"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
